import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmDialog";
import "./ProductResults.css";
import zilchLogoBlack from "../assets/logos/zilchlogoblack.png";
import { CERT_LOGOS, certMonogram } from "../constants/certLogos";
import { PRODUCT_CERT_LABELS, NO_FILE_CERTS } from "../constants/productCerts";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

// Credentials attached to the assessment itself (the product, or the factory
// that makes it) live in ../constants/productCerts. These are the ones held
// org-wide by the supplier.
const BUSINESS_CERT_LABELS = {
  bcorp: "B Corp Certified",
  ecovadis: "EcoVadis",
  climate_active_carbon_neutral: "Climate Active Carbon Neutral",
  ecovadis_bronze: "Ecovadis Bronze",
  ecovadis_silver: "Ecovadis Silver",
  ecovadis_gold: "Ecovadis Gold",
  ecovadis_platinum: "Ecovadis Platinum",
  iso14001: "ISO 14001",
  iso9001: "ISO 9001",
  iso45001: "ISO 45001",
  apco_member: "APCO Member",
  sedex_member: "Sedex Member",
  supply_nation_member: "Supply Nation Member",
  supply_nation_registered: "Supply Nation Registered",
  supply_nation_certified: "Supply Nation Certified",
};

const parseJson = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
};

// The donut is drawn in a fixed 240-unit coordinate space and scaled to whatever
// room the layout gives it, so the CSS can resize the chart without the geometry
// needing to be recomputed.
const DONUT_BOX = 240;

export default function ProductResults() {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { supplierInfo, billing, refreshBillingInfo } = useAuth();
  const { confirm, alert } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(null);
  const [product, setProduct] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [endingTrial, setEndingTrial] = useState(false);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  // How many of the donut's centre images have failed to load; each failure
  // hands over to the next one (see centerImage below).
  const [centerImageFailures, setCenterImageFailures] = useState(0);
  const [isLinkedAssessment, setIsLinkedAssessment] = useState(false);
  const [supplierInactive, setSupplierInactive] = useState(false);
  const [resultsPrivate, setResultsPrivate] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showScrollCue, setShowScrollCue] = useState(false);

  // Extract stable values to prevent unnecessary re-renders. Sanitize the URL param to the
  // bare UUID — some share targets concatenate the share text onto the url, which would
  // otherwise poison supplier_id (and every subsequent share link generated from it).
  const supplierIdFromParams = useMemo(() => {
    const raw = searchParams.get("supplier_id");
    if (!raw) return null;
    const match = raw.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
    );
    return match ? match[0] : raw.trim();
  }, [searchParams]);
  const supplierIdFromContext = useMemo(() => supplierInfo?.supplier_id, [supplierInfo?.supplier_id]);
  const supplierId = supplierIdFromParams || supplierIdFromContext;

  // Use ref to prevent multiple simultaneous fetches
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const lowerRef = useRef(null);

  // Scroll to top when component mounts or productId changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  // The layout aims to leave the panel headings peeking below the hero, which is
  // signal enough on its own. This is the backstop for when it can't — a short
  // window, a title that wraps, an unusually tall hero — so the threshold is
  // tight: cue only when there is essentially nothing showing down there.
  useEffect(() => {
    const update = () => {
      const el = lowerRef.current;
      if (!el) {
        setShowScrollCue(false);
        return;
      }
      const headingsTop = el.getBoundingClientRect().top;
      setShowScrollCue(headingsTop > window.innerHeight - 40);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [loading, formData]);

  const scrollToPanels = () => {
    lowerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    // Prevent multiple simultaneous fetches
    if (fetchingRef.current) {
      return;
    }

    if (!supplierId || !productId) {
      setError("Missing supplier or product information");
      setLoading(false);
      return;
    }

    const fetchProductResults = async () => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      fetchingRef.current = true;

      try {
        setLoading(true);
        setError("");
        setSupplierInactive(false);
        setResultsPrivate(false);
        setCenterImageFailures(0); // New product, start from its own photo again

        // Fetch the assessment data
        const { data: assessmentResponse } = await axios.get(
          `${API_BASE_URL}/lca/assessments`,
          {
            params: {
              supplier_id: supplierId,
              product_id: productId,
            },
            signal: abortController.signal,
          }
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const assessment = assessmentResponse?.data;
        
        if (!assessment) {
          setError("No assessment found for this product");
          setLoading(false);
          fetchingRef.current = false;
          return;
        }

        // Store assessment data for PDF generation
        setAssessment(assessment);

        // Check if assessment is linked (has _linked_from_product_id from getLcaAssessment)
        // This indicates the assessment data is from a source assessment
        setIsLinkedAssessment(assessment._linked_from_product_id !== undefined);

        // Fetch product details for display
        try {
          const { data: productResponse } = await axios.get(
            `${API_BASE_URL}/products/${productId}`,
            {
              params: { supplier_id: supplierId },
              signal: abortController.signal,
            }
          );
          if (!abortController.signal.aborted) {
            setProduct(productResponse?.data || null);
          }
        } catch (e) {
          if (!abortController.signal.aborted && e.name !== 'CanceledError') {
            console.warn("Could not load product details:", e);
          }
        }

        // Transform assessment data to formData structure
        if (!abortController.signal.aborted) {
          // Keep this helper local to the effect to avoid react-hooks/exhaustive-deps warnings
          // (it doesn't need to be stable across renders).
          const transformAssessmentToFormData = (assessment) => {
            // Calculate results breakdown from assessment data
            const breakdown = calculateBreakdown(assessment);
            
            // Explicitly sum all emission categories
            const totalEmissions = 
              (breakdown.materials || 0) + 
              (breakdown.packaging || 0) + 
              (breakdown.decoration || 0) + 
              (breakdown.transportation || 0) + 
              (breakdown.manufacturing || 0);
          
            // Helper: normalize assemblyLocation to ensure it's always a string (country code)
            const normalizeAssemblyLocation = (location) => {
              if (!location) return "";
              // If it's an object with a code property, extract the code
              if (typeof location === 'object' && location !== null) {
                return location.code || "";
              }
              // Otherwise return as string
              return String(location);
            };
          
            return {
              productInfo: {
                name: assessment.product_name || "",
                sku: assessment.sku || "",
                category: assessment.parent_category_slug || assessment.category || "",
                weight: assessment.weight_grams || "",
                productType: assessment.product_type || "manufactured",
                supplierDataAvailable: !!assessment.supplier_data_available,
                supplierEmissions: assessment.supplier_emissions || "",
                supplierEmissionsFileName: assessment.supplier_emissions_file_name || "",
                isExternallyAssessed: !!assessment.is_externally_assessed,
                externalAssessmentSource: assessment.external_assessment_source || "",
                externalAssessmentDocPath: assessment.external_assessment_doc_path || "",
                externalAssessmentDocName: assessment.external_assessment_doc_name || "",
                externalAssessmentDocUrl: assessment.external_assessment_doc_url || "",
              },
              materialAcquisition: {
                assessmentType: assessment.material_assessment_type || "finished",
                // Country of origin — the authoritative "where it's made" value.
                supplierLocation: assessment.supplier_location || "",
                parts: (() => {
                  // Handle parts - might be array, JSON string, or null/undefined
                  let parts = assessment.parts;
                  if (!parts) return [];
                  if (typeof parts === 'string') {
                    try {
                      parts = JSON.parse(parts);
                    } catch (e) {
                      console.error('Error parsing parts JSON:', e);
                      return [];
                    }
                  }
                  return Array.isArray(parts) ? parts : [];
                })(),
                finishedProduct: {
                  category: assessment.finished_subcategory_slug || assessment.finished_category || "",
                  industryAverage: assessment.finished_industry_average || 0,
                  parentCategorySlug: assessment.finished_parent_category_slug || "",
                  subcategorySlug: assessment.finished_subcategory_slug || "",
                  productSlug: assessment.finished_product_slug || "",
                  quality: assessment.finished_quality || "",
                  composition: assessment.finished_composition || [],
                  processes: assessment.finished_processes || [],
                },
                processEmissions: assessment.process_emissions || {
                  printing: false,
                  embroidery: false,
                  engraving: false,
                  other: "",
                },
              },
              packaging: {
                materials: Array.isArray(assessment.packaging_materials)
                  ? assessment.packaging_materials
                  : [],
              },
              assemblyDistribution: {
                manufacturing: {
                  energyConsumption: assessment.manufacturing_energy_consumption_kwh || "",
                  assemblyLocation: normalizeAssemblyLocation(assessment.assembly_location),
                  energySource: assessment.energy_source || "grid",
                },
                transportation: Array.isArray(assessment.transport_legs) && assessment.transport_legs.length > 0
                  ? assessment.transport_legs.map((leg) => ({
                      mode: leg.mode || "sea_container",
                      distance: leg.distance_km || "",
                      originCountry: leg.origin_country || "",
                      destinationCountry: leg.destination_country || "",
                    }))
                  : [{
                      mode: assessment.transportation_mode || "sea_container",
                      distance: assessment.transportation_distance_km || "",
                      originCountry: assessment.origin_country || "",
                      destinationCountry: assessment.destination_country || "",
                    }],
              },
              results: {
                totalEmissions: Number(totalEmissions.toFixed(4)),
                breakdown,
                // recommendations: generateRecommendations(breakdown),
              },
              decorationProcesses: Array.isArray(assessment.decoration_processes)
                ? assessment.decoration_processes
                : [],
            };
          };

          let transformedData = transformAssessmentToFormData(assessment);
          
          
          // For finished product assessments, check if we have composition saved, otherwise fetch it
          if (assessment.material_assessment_type === "finished") {
            // First, check if composition is already saved in the assessment
            const savedComposition = assessment.finished_composition;
            if (savedComposition && Array.isArray(savedComposition) && savedComposition.length > 0) {
              transformedData.materialAcquisition.finishedProduct.composition = savedComposition;
            } else {
              // Try to get from assessment first, then from transformedData
              const subcategorySlug = assessment.finished_subcategory_slug || transformedData.materialAcquisition.finishedProduct.subcategorySlug;
              const productSlug = assessment.finished_product_slug || transformedData.materialAcquisition.finishedProduct.productSlug;
              const quality = assessment.finished_quality || transformedData.materialAcquisition.finishedProduct.quality;
              
              
              
              if (subcategorySlug && productSlug && quality) {
                try {
                const { data: variantResponse } = await axios.get(
                  `${API_BASE_URL}/taxonomy/subcategories/${subcategorySlug}/finished-products/${productSlug}`,
                  {
                    params: { quality },
                    signal: abortController.signal
                  }
                );
                
                
                if (!abortController.signal.aborted && variantResponse?.data) {
                  const variant = variantResponse.data;
                  
                  // Always update composition from the database (most up-to-date)
                  if (variant.composition) {
                    // Handle both array and parsed JSON string
                    let composition = variant.composition;
                    if (typeof composition === 'string') {
                      try {
                        composition = JSON.parse(composition);
                      } catch (e) {
                        console.error('[ProductResults] Failed to parse composition string:', e);
                      }
                    }
                    
                    if (Array.isArray(composition) && composition.length > 0) {
                      transformedData.materialAcquisition.finishedProduct.composition = composition;
                    } else {
                      console.warn('[ProductResults] Composition is not a valid array:', composition);
                    }
                  } else {
                    console.warn('[ProductResults] No composition in variant data');
                  }
                  
                  // Update processes if available
                  if (variant.processes && Array.isArray(variant.processes) && variant.processes.length > 0) {
                    transformedData.materialAcquisition.finishedProduct.processes = variant.processes;
                  }
                  
                  // Update industryAverage if available
                  if (variant.ef_kgco2_per_kg) {
                    transformedData.materialAcquisition.finishedProduct.industryAverage = variant.ef_kgco2_per_kg;
                  }
                } else {
                  console.warn('[ProductResults] No data in variant response:', variantResponse);
                }
                } catch (e) {
                  if (!abortController.signal.aborted && e.name !== 'CanceledError') {
                    console.error("[ProductResults] Could not fetch finished product composition:", e);
                    console.error("[ProductResults] Error response:", e.response?.data);
                    console.error("[ProductResults] Error status:", e.response?.status);
                  }
                }
              } else {
                console.warn('[ProductResults] Missing required fields:', { subcategorySlug, productSlug, quality });
              }
            }
          }
          
          setFormData(transformedData);
        }
      } catch (err) {
        if (!abortController.signal.aborted && err.name !== 'CanceledError') {
          if (err.response?.status === 402 && err.response?.data?.error === 'supplier_inactive') {
            setSupplierInactive(true);
          } else if (err.response?.status === 403 && err.response?.data?.error === 'results_private') {
            setResultsPrivate(true);
          } else {
            console.error("Error fetching product results:", err);
            setError("Failed to load product results. This product may not have completed calculations yet.");
          }
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
        fetchingRef.current = false;
      }
    };

    fetchProductResults();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      fetchingRef.current = false;
    };
  }, [productId, supplierId]);

  // Calculate breakdown from assessment
  const calculateBreakdown = (assessment) => {
    const weightGrams = Number(assessment.weight_grams || 0);
    const weightKg = weightGrams > 0 ? weightGrams / 1000 : 0;

    // Materials emissions (use stored value if available, otherwise calculate)
    let materialsEmissionsKg = 0;
    if (assessment.material_emissions_kg !== undefined && assessment.material_emissions_kg !== null) {
      // Use pre-calculated value from database
      materialsEmissionsKg = Number(assessment.material_emissions_kg || 0);
    } else if ((assessment.material_assessment_type || "finished") === "finished") {
      // Fallback: calculate from finished product data
      const efFinished = Number(assessment.finished_industry_average || 0);
      materialsEmissionsKg = efFinished * weightKg;
    } else if (assessment.material_assessment_type === "parts" && Array.isArray(assessment.parts)) {
      // Fallback: calculate from parts (this would need emission factors per material)
      // For now, this case returns 0 unless stored value exists
      materialsEmissionsKg = 0;
    }

    // Packaging emissions (use stored value if available)
    const packagingEmissionsKg = Number(assessment.packaging_emissions_kg || 0);

    // Decoration emissions (use stored value if available)
    const decorationEmissionsKg = Number(assessment.decoration_emissions_kg || 0);

    // Transportation emissions (use stored value if available)
    const transportationEmissionsKg = Number(assessment.transport_emissions_kg || 0);

    // Manufacturing emissions (use stored value if available, or 0 if not modeled)
    const manufacturingEmissionsKg = Number(assessment.manufacturing_emissions_kg || 0);

    // Ensure all values are valid numbers
    const materials = Number.isFinite(materialsEmissionsKg) ? Number(materialsEmissionsKg.toFixed(4)) : 0;
    const packaging = Number.isFinite(packagingEmissionsKg) ? Number(packagingEmissionsKg.toFixed(4)) : 0;
    const decoration = Number.isFinite(decorationEmissionsKg) ? Number(decorationEmissionsKg.toFixed(4)) : 0;
    const transportation = Number.isFinite(transportationEmissionsKg) ? Number(transportationEmissionsKg.toFixed(4)) : 0;
    const manufacturing = Number.isFinite(manufacturingEmissionsKg) ? Number(manufacturingEmissionsKg.toFixed(4)) : 0;

    return {
      materials,
      packaging,
      decoration,
      transportation,
      manufacturing,
    };
  };

  // Generate recommendations based on emissions breakdown
  // const generateRecommendations = (breakdown) => {
  //   const recommendations = [];

  //   if (breakdown.materials > breakdown.transportation) {
  //     recommendations.push(
  //       "Optimise material use through lightweighting, recycled inputs, or multi-purpose design to reduce embodied carbon per unit produced."
  //     );
  //   }
  //   if (breakdown.materials > 15) {
  //     recommendations.push(
  //       "Implement take-back, recycling, or composting programs to recover materials and minimise landfill emissions." 
  //     );
  //   }

  //   if (breakdown.decoration > 5) {
  //     recommendations.push(
  //       "Streamline production steps, reduce waste heat, and optimise batch sizing or changeovers to minimise idle energy and material losses." 
  //     );
  //   }

  //   if (breakdown.transportation > 50) {
  //     recommendations.push(
  //       "Shorten transport distances, consolidate shipments, or adopt low-carbon freight (rail, sea, or electric fleets)."
  //     );
  //   }

  //   if (breakdown.packaging > 20) {
  //     recommendations.push(
  //       "Minimise packaging volume, switch to recycled or mono-material formats, and enable consumer-side recycling or refill systems."
  //     );
  //   }

  //   if (breakdown.manufacturing > 20) {
  //     recommendations.push(
  //       "Design for durability, repairability, or reuse to extend product lifespan and reduce production turnover."
  //     );
  //   }
  //   if (breakdown.manufacturing > 10) {
  //     recommendations.push(
  //       "Engage tier-1 and tier-2 suppliers to disclose emissions data, adopt science-based targets, and share verified reductions through digital reporting systems."
  //     );
  //   }

  //   return recommendations;
  // };

  // Always display in kg; values under 1 kg get one extra decimal of precision.
  const formatEmissions = (valueKg, digits = 2) => {
    const num = parseFloat(valueKg);
    if (Number.isNaN(num)) return { value: "-", unit: "kg CO₂e" };
    const decimals = num > 0 && num < 1 ? digits + 1 : digits;
    return { value: num.toFixed(decimals), unit: "kg CO₂e" };
  };

  // Turn a slug or raw label into a display name ("wool_fiber" -> "Wool Fiber").
  const formatMaterialName = (raw) => {
    const value = `${raw || ""}`.trim();
    if (!value) return "";
    return value
      .split(/[\s_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Every material the product is made of — the full finished-product composition,
  // or the material of each part (heaviest first), de-duplicated.
  const getMaterials = () => {
    if (!formData) {
      return "N/A";
    }

    const assessmentType = formData?.materialAcquisition?.assessmentType || "finished";
    
    // For finished products, use composition if available
    if (assessmentType === "finished") {
      const composition = formData?.materialAcquisition?.finishedProduct?.composition;
      
      if (composition && Array.isArray(composition) && composition.length > 0) {
        const materials = composition
          .map((comp) =>
            formatMaterialName(typeof comp === 'object' ? comp?.material : comp)
          )
          .filter(Boolean);
        const unique = [...new Set(materials)];
        if (unique.length > 0) return unique.join(", ");
      }
      // Fall back to subcategory slug
      return (
        formatMaterialName(
          formData?.materialAcquisition?.finishedProduct?.subcategorySlug
        ) || "N/A"
      );
    }
    
    // For parts-based assessments, list every part's material, heaviest part first
    if (assessmentType === "parts") {
      let parts = formData?.materialAcquisition?.parts;

      // Handle case where parts might be a JSON string (shouldn't happen after transform, but just in case)
      if (typeof parts === 'string') {
        try {
          parts = JSON.parse(parts);
        } catch (e) {
          console.error('Error parsing parts JSON:', e);
          parts = [];
        }
      }

      if (parts && Array.isArray(parts) && parts.length > 0) {
        const materials = [...parts]
          .sort(
            (a, b) =>
              Number(b?.weight_grams || 0) - Number(a?.weight_grams || 0)
          )
          .map((part) =>
            formatMaterialName(
              part?.material_subcategory || part?.material_category
            )
          )
          .filter(Boolean);

        // Two parts can share a material (e.g. a lid and a base both in PP);
        // list each distinct material once.
        const unique = [...new Set(materials)];
        if (unique.length > 0) return unique.join(", ");
      }
    }

    // Final fallback
    return (
      formatMaterialName(
        formData?.materialAcquisition?.finishedProduct?.subcategorySlug
      ) || "N/A"
    );
  };

  // Download the server-rendered PDF. Server is the single source of truth — see
  // GET /api/pdf/:supplierId/:productId, which lazily regenerates if the storage
  // object is missing.
  const handleDownloadPDF = async () => {
    setGeneratingPDF(true);
    try {
      // White-label: when a distributor downloads a PDF for a product they pulled from a
      // supplier (so the URL's supplier_id is the source supplier, not theirs), ask the
      // server to stamp the PDF with the distributor's own name + business certs. The
      // server validates the distributor↔product link before honouring it.
      const params = { download: 1 };
      const viewerOrgId = supplierInfo?.supplier_id;
      const isDistributor = supplierInfo?.account_type === 'Distributor';
      if (isDistributor && viewerOrgId && viewerOrgId !== supplierId) {
        params.brandOrgId = viewerOrgId;
      }

      const res = await axios.get(
        `${API_BASE_URL}/pdf/${supplierId}/${productId}`,
        { params, responseType: 'blob' }
      );

      const filename = (() => {
        const header = res.headers?.['content-disposition'] || '';
        const match = header.match(/filename="?([^";]+)"?/i);
        return match?.[1] || `${productId}.pdf`;
      })();

      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert({ title: 'Download failed', message: 'We could not generate your PDF. Please try again.' });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Downloading the PDF requires a paid (not trialing) subscription. For trial
  // users, ending the trial now charges their saved card; once active we download.
  const handleEndTrialAndDownload = async () => {
    const ok = await confirm({
      title: 'End your free trial now?',
      message: 'Downloading the audit-ready PDF ends your free trial and charges your saved card for your selected plan straight away.',
      confirmLabel: 'End trial and download',
      cancelLabel: 'Not yet',
    });
    if (!ok) return;
    setEndingTrial(true);
    try {
      await axios.post(`${API_BASE_URL}/billing/end-trial`, {});
      if (refreshBillingInfo) await refreshBillingInfo();
      await handleDownloadPDF();
    } catch (err) {
      console.error('Error ending trial:', err);
      alert({
        title: 'Could not start your subscription',
        message: err.response?.data?.error || 'Please try again, or contact support if it keeps happening.',
      });
    } finally {
      setEndingTrial(false);
    }
  };

  // Build a public, shareable URL for this assessment. supplier_id is included so the
  // recipient can load the same results without being signed in as the owner.
  const buildShareUrl = () => {
    const url = new URL(`/product-results/${productId}`, window.location.origin);
    if (supplierId) url.searchParams.set("supplier_id", supplierId);
    return url.toString();
  };

  const handleShare = async () => {
    const shareUrl = buildShareUrl();
    const shareTitle = product?.name || formData?.productInfo?.name || "Product Footprint Assessment";

    // Prefer the native share sheet (mobile / supported browsers); fall back to clipboard.
    // Pass only the title + url — do NOT pass a `text` field: share targets that flatten the
    // payload to a single string concatenate `url` + `text`, which corrupts the link (the
    // share text ends up glued onto the supplier_id query param).
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user dismissed the share sheet
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy share link:", err);
      alert({ title: 'Copy failed', message: 'Please copy the URL from your browser address bar instead.' });
    }
  };

  if (loading) {
    return (
      <div className="results-page">
        <div className="results-loading">
          <div className="results-spinner"></div>
          <p>Loading carbon footprint...</p>
        </div>
      </div>
    );
  }

  if (supplierInactive) {
    return (
      <div className="results-page">
        <div className="results-error">
          <h2>Assessment Data Unavailable</h2>
          <p>This supplier's assessment data is currently unavailable. The supplier may need to renew their subscription to share this data.</p>
        <p>Contact the supplier directly for more information.</p>
        </div>
      </div>
    );
  }

  // The owner has switched their results pages off. Someone landing here from an
  // older shared link isn't necessarily an outsider — a colleague who simply isn't
  // signed in sees this too, so point them at the login rather than dead-ending.
  if (resultsPrivate) {
    return (
      <div className="results-page">
        <div className="results-error">
          <h2>This Assessment Is Private</h2>
          <p>
            The organisation behind this product has chosen not to share its footprint
            results publicly. Contact them directly if you need this data.
          </p>
          {!supplierInfo && (
            <Link to="/login" className="results-btn results-btn-primary">
              Sign in
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="results-page">
        <div className="results-error">
          <h2>Assessment Not Available</h2>
          <p>{error || "Unable to load product results."}</p>
          <Link to="/" className="results-btn results-btn-primary">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const breakdown = formData?.results?.breakdown || {};

  // Combine decoration with materials for display
  const displayBreakdown = {
    materials: (breakdown.materials || 0) + (breakdown.decoration || 0),
    packaging: breakdown.packaging || 0,
    transportation: breakdown.transportation || 0,
    manufacturing: breakdown.manufacturing || 0,
  };

  // Donut chart setup - modern Zilch palette
  const palette = {
    materials: "#06a457",
    packaging: "#3b82f6",
    transportation: "#f59e0b",
    manufacturing: "#8b5cf6",
  };

  // Recalculate total from breakdown to ensure accuracy
  const total = (breakdown.materials || 0) + 
                (breakdown.packaging || 0) + 
                (breakdown.decoration || 0) + 
                (breakdown.transportation || 0) + 
                (breakdown.manufacturing || 0);
  
  const orderedKeys = ["materials", "packaging", "transportation", "manufacturing"];
  const segments = orderedKeys
    .map((k) => ({ key: k, value: Number(displayBreakdown[k] || 0), color: palette[k] }))
    .filter((s) => Number.isFinite(s.value) && s.value >= 0);

  const DonutChart = ({ data, strokeWidth = 26, centerImage, onSegmentHover, hoveredKey, onImageError }) => {
    const size = DONUT_BOX;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const sum = data.reduce((acc, d) => acc + d.value, 0);

    let cumulative = 0;
    const arcs = data.map((d, index) => {
      const fraction = sum > 0 ? d.value / sum : 0;
      const length = fraction * circumference;
      const dasharray = `${length} ${circumference - length}`;
      const dashoffset = circumference - (cumulative / sum) * circumference;
      
      // Calculate angle for tooltip positioning
      const startAngle = (cumulative / sum) * 360 - 90;
      const midAngle = startAngle + (fraction * 180);
      
      cumulative += d.value;
      return { ...d, dasharray, dashoffset, midAngle, index };
    });

    // Calculate tooltip position for arc segment
    const getTooltipPosition = (arc) => {
      const radian = (arc.midAngle * Math.PI) / 180;
      const tooltipRadius = radius + strokeWidth / 2 + 30;
      const x = Math.cos(radian) * tooltipRadius;
      const y = Math.sin(radian) * tooltipRadius;
      return { x: size / 2 + x, y: size / 2 + y };
    };

    return (
      <div className="results-donut-wrapper">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${size} ${size}`}
          style={{ overflow: 'visible', display: 'block' }}
        >
          <g transform={`translate(${size / 2}, ${size / 2})`}>
            <circle r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
            {arcs.map((arc) => {
              const isHovered = hoveredKey === arc.key || hoveredKey === null;
              const opacity = hoveredKey === null ? 1 : (isHovered ? 1 : 0.25);
              const strokeWidthHover = isHovered && hoveredKey === arc.key ? strokeWidth + 4 : strokeWidth;
              
              return (
                <g key={arc.key}>
                  <circle
                    r={radius}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={strokeWidthHover}
                    strokeDasharray={arc.dasharray}
                    strokeDashoffset={isFinite(arc.dashoffset) ? arc.dashoffset : 0}
                    transform="rotate(-90)"
                    strokeLinecap="round"
                    opacity={opacity}
                    style={{ cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}
                    onMouseEnter={() => onSegmentHover(arc.key)}
                    onMouseLeave={() => onSegmentHover(null)}
                  />
                </g>
              );
            })}
          </g>
        </svg>
        <div className="results-donut-center">
          <img
            src={centerImage.src}
            alt={centerImage.alt}
            loading="eager"
            style={{
              width: '100%',
              height: '100%',
              objectFit: centerImage.isLogo ? 'contain' : 'cover',
              padding: centerImage.isLogo ? '12%' : 0,
              objectPosition: 'center',
              display: 'block',
              visibility: 'visible',
              opacity: 1
            }}
            onError={() => {
              if (onImageError) onImageError();
            }}
          />
        </div>
        {hoveredKey && (() => {
          const arc = arcs.find(a => a.key === hoveredKey);
          if (!arc) return null;
          const pos = getTooltipPosition(arc);
          return (
            <div
              className="results-chart-tooltip-wrapper"
              style={{
                position: 'absolute',
                left: `${(pos.x / size) * 100}%`,
                top: `${(pos.y / size) * 100}%`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 10
              }}
            >
              <div className="results-chart-tooltip-dot" style={{ background: arc.color }}></div>
              <div className="results-chart-tooltip-content">
                <div className="results-chart-tooltip-value" style={{ color: arc.color }}>
                  {formatEmissions(arc.value).value} {formatEmissions(arc.value).unit.split(' ')[0]}
                </div>
                <div className="results-chart-tooltip-label">{arc.key}</div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // Branding the report — the org's name, logo and sustainability journey — is a
  // Pro+ feature. The server resolves it from the *owning* org's plan (not the
  // viewer's, so a shared link reads the same for everyone) and strips the values
  // it doesn't grant; this flag keeps the page honest if an older payload still
  // carries them. Absent flag = don't hide anything.
  const brandingEnabled = assessment?.branding_enabled !== false;

  // Credentials come from two places: the assessment carries the product's own
  // (and its factory's) marks, the supplier row carries the org-wide ones.
  const credentials = (() => {
    // Every credential the owner selected shows; one with a proof file links
    // to it. Claim-based decoration credentials (FSC/PEFC, ABA) never link.
    const productSI = parseJson(assessment?.social_impact);
    const productCertFiles = productSI?.certFiles || {};
    const productCerts = [...new Set(productSI?.product || [])]
      .map((id) => ({
        id,
        label:
          id === "other"
            ? productSI?.productOther || "Other"
            : PRODUCT_CERT_LABELS[id] || id,
        file: productCertFiles[id],
        claimOnly: NO_FILE_CERTS.includes(id),
      }));

    const supplierSI = parseJson(assessment?.supplier_social_impact);
    const businessCertFiles = supplierSI?.certFiles || {};
    const businessCerts = [...new Set(supplierSI?.business || [])]
      .map((id) => ({
        id,
        label:
          id === "other"
            ? supplierSI?.businessOther || "Other"
            : BUSINESS_CERT_LABELS[id] || id,
        file: businessCertFiles[id],
        claimOnly: false,
      }));

    return {
      product: productCerts,
      business: businessCerts,
      // Credentials stay on every plan; the free-text journey is Pro+ only.
      statement: brandingEnabled ? supplierSI?.statement || "" : "",
    };
  })();

  // Compliance documents: the org's modern slavery statement, code of conduct,
  // environmental policy and any register filing. Not credentials — nothing
  // certifies them and they carry no logo — so they render as a list of
  // documents below the badges, never as a tile beside ISO 14001. The server
  // decides which ones may appear here at all (services/orgDocuments.js): only
  // documents the organisation itself uploaded, filed or published, never a
  // scan's unbacked guess. Every entry arrives with a resolved, live URL.
  const complianceDocs = Array.isArray(assessment?.compliance_documents)
    ? assessment.compliance_documents.filter((doc) => doc?.url && doc?.label)
    : [];

  const CertTile = ({ cert }) => {
    const logo = CERT_LOGOS[cert.id] || null;
    const tile = (
      <div className="results-cert-tile">
        <div className="results-cert-tile-logo-wrap">
          {logo ? (
            <img
              src={logo}
              alt={`${cert.label} logo`}
              className="results-cert-tile-logo"
            />
          ) : (
            <div className="results-cert-tile-monogram">
              {certMonogram(cert.label)}
            </div>
          )}
        </div>
        <div className="results-cert-tile-name">{cert.label}</div>
      </div>
    );
    // Claim-based marks carry no verification file, so they are never linked.
    return cert.file?.url && !cert.claimOnly ? (
      <a
        href={cert.file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="results-cert-tile-link"
        title={`View ${cert.label} verification`}
      >
        {tile}
      </a>
    ) : (
      tile
    );
  };

  // Only the owning org ever loads this page while results are private, so the
  // warning is theirs alone — everyone else was stopped at the fetch.
  const isOwnerViewingPrivate =
    assessment?.results_public === false &&
    supplierInfo?.supplier_id === supplierId;

  const supplierName = brandingEnabled ? assessment?.supplier_name || "" : "";
  const supplierLogo = brandingEnabled ? assessment?.supplier_logo_url || "" : "";

  // The middle of the donut: the product's own photo, else the owning org's
  // logo (only sent when it can brand its reports), else ours. Each one that
  // fails to load hands over to the next.
  const centerImages = [
    product?.images?.[0] && { src: product.images[0], alt: "Product", isLogo: false },
    supplierLogo && { src: supplierLogo, alt: `${supplierName || "Supplier"} logo`, isLogo: true },
    { src: zilchLogoBlack, alt: "Zilch logo", isLogo: true },
  ].filter(Boolean);
  const centerImage =
    centerImages[Math.min(centerImageFailures, centerImages.length - 1)];
  // The supplier's public compliance profile (tryzilch.com/company/…). The
  // server only sends it for a live profile on a branded results page.
  const supplierProfileUrl = /^https:\/\//.test(assessment?.supplier_profile_url || "")
    ? assessment.supplier_profile_url
    : "";

  // The org panel earns its column if the supplier has credentials, compliance
  // documents, a sustainability statement, or any combination — any one alone
  // is enough to show it.
  const hasBusinessCerts = credentials.business.length > 0;
  const hasComplianceDocs = complianceDocs.length > 0;
  const hasOrgPanel = hasBusinessCerts || hasComplianceDocs || !!credentials.statement;

  // Named for what the panel actually leads with. Documents don't get called
  // credentials when they're all there is: a policy is a policy.
  const orgPanelTitle = hasBusinessCerts
    ? "Our Credentials"
    : hasComplianceDocs
      ? "Our Compliance Documents"
      : "Our Sustainability Journey";
  const orgPanelSubtitle = hasBusinessCerts
    ? `Held by ${supplierName || "this organisation"}`
    : hasComplianceDocs
      ? `On file for ${supplierName || "this organisation"}`
      : `In ${supplierName || "this organisation"}'s own words`;

  return (
    <div className="results-shell">
      {/* The hero claims the first screen; the credential and key-data panels
          sit below it as scroll targets, then the footer. */}
      <div className="results-screen">
        <div className="results-topbar">
          {isOwnerViewingPrivate && (
            <Link to="/profile" className="results-private-pill" title="Change this in Settings">
              <svg
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M6 9V6.5a4 4 0 018 0V9M5 9h10v7H5V9z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Private — this link won't open for anyone else</span>
            </Link>
          )}
          <button
            className="results-btn-share"
            onClick={handleShare}
            title="Share this assessment"
          >
            {shareCopied ? (
              <>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 10.5L8 14.5L16 5.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Link copied</span>
              </>
            ) : (
              <>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="18"
                    cy="5"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle
                    cx="6"
                    cy="12"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle
                    cx="18"
                    cy="19"
                    r="3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Share</span>
              </>
            )}
          </button>
        </div>

        <div className="results-stage">
          <div className="results-hero">
            {/* Left Side - Product & Main Number */}
            <div className="results-hero-left">
              {(supplierLogo || supplierName) && (() => {
                const org = (
                  <>
                    {supplierLogo && (
                      <img
                        className="results-org-logo"
                        src={supplierLogo}
                        alt={`${supplierName || "Supplier"} logo`}
                      />
                    )}
                    {supplierName && (
                      <span className="results-org-name">{supplierName}</span>
                    )}
                  </>
                );
                return supplierProfileUrl ? (
                  <a
                    className="results-org results-org-link"
                    href={supplierProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`View ${supplierName || "this supplier"}'s compliance profile`}
                  >
                    {org}
                  </a>
                ) : (
                  <div className="results-org">{org}</div>
                );
              })()}

              <div className="results-product-header">
                <h1 className="results-product-title">
                  {product?.name || formData?.productInfo?.name || "Product"}
                </h1>
                <div className="results-product-subtitle">
                  Product Footprint Assessment
                </div>
                {isLinkedAssessment && (
                  <div className="mt-2">
                    <span
                      className="badge bg-info"
                      title="This product shares assessment data with another SKU"
                    >
                      Linked Assessment
                    </span>
                  </div>
                )}
              </div>

              <div className="results-emissions-hero">
                <div className="results-emissions-number">
                  <span className="results-number-value">
                    {formatEmissions(total).value}
                  </span>
                  <span className="results-number-unit">
                    {formatEmissions(total).unit}
                  </span>
                </div>
                <div className="results-emissions-description">
                  Total emissions per unit
                </div>
              </div>

              {/* Product Certifications — shown inline under total */}
              {credentials.product.length > 0 && (
                <div className="results-inline-certs">
                  <div className="results-eyebrow">
                    Product &amp; Manufacturer Credentials
                  </div>
                  <div className="results-cert-row">
                    {credentials.product.map((cert, i) => (
                      <CertTile key={`${cert.id}-${i}`} cert={cert} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Chart & Breakdown */}
            <div className="results-hero-right">
              <div className="results-visual-section">
                <div className="results-chart-wrapper">
                  <DonutChart
                    data={segments}
                    centerImage={centerImage}
                    onSegmentHover={setHoveredSegment}
                    hoveredKey={hoveredSegment || hoveredLegend}
                    onImageError={() =>
                      setCenterImageFailures((n) => Math.min(n + 1, centerImages.length - 1))
                    }
                  />
                </div>

                {segments.length > 0 && total > 0 && (
                  <div className="results-breakdown-compact">
                    {orderedKeys
                      .filter((k) => displayBreakdown[k] > 0)
                      .map((k) => {
                        const isHovered =
                          hoveredSegment === k || hoveredLegend === k;
                        const isDimmed =
                          (hoveredSegment !== null || hoveredLegend !== null) &&
                          !isHovered;

                        return (
                          <div
                            key={k}
                            className={`results-breakdown-compact-item ${isHovered ? "results-breakdown-compact-item-hovered" : ""} ${isDimmed ? "results-breakdown-compact-item-dimmed" : ""}`}
                            onMouseEnter={() => setHoveredLegend(k)}
                            onMouseLeave={() => setHoveredLegend(null)}
                          >
                            <div
                              className="results-breakdown-compact-indicator"
                              style={{
                                background: palette[k],
                                opacity: isHovered ? 1 : isDimmed ? 0.3 : 0.8,
                              }}
                            ></div>
                            <div className="results-breakdown-compact-content">
                              <span className="results-breakdown-compact-label">
                                {k}
                              </span>
                              <span className="results-breakdown-compact-value">
                                {
                                  formatEmissions(displayBreakdown[k] || 0)
                                    .value
                                }{" "}
                                {
                                  formatEmissions(
                                    displayBreakdown[k] || 0,
                                  ).unit.split(" ")[0]
                                }
                                <span className="results-breakdown-compact-percent">
                                  {total > 0
                                    ? (
                                        (Number(displayBreakdown[k] || 0) /
                                          total) *
                                        100
                                      ).toFixed(0)
                                    : 0}
                                  %
                                </span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            ref={lowerRef}
            className={`results-lower${!hasOrgPanel ? " results-lower-single" : ""}`}
          >
            {/* Business credentials + sustainability journey — held by the
                organisation, not the product. */}
            {hasOrgPanel && (
              <section className="results-panel">
                <h2 className="results-panel-title">
                  {orgPanelTitle}
                  <span className="results-panel-subtitle">{orgPanelSubtitle}</span>
                </h2>
                <div className="results-card">
                  {hasBusinessCerts && (
                    <div className="results-cert-grid">
                      {credentials.business.map((cert, i) => (
                        <CertTile key={`${cert.id}-${i}`} cert={cert} />
                      ))}
                    </div>
                  )}
                  {hasComplianceDocs && (
                    <div
                      className={`results-docs${hasBusinessCerts ? "" : " results-docs-solo"}`}
                    >
                      {/* Titled only when something sits above it — alone in the
                          card, the panel heading has already said this. */}
                      {hasBusinessCerts && (
                        <div className="results-statement-title">
                          Compliance Documents
                        </div>
                      )}
                      <ul className="results-doc-list">
                        {complianceDocs.map((doc, i) => (
                          <li key={`${doc.standard_id || doc.label}-${i}`}>
                            <a
                              className="results-doc-link"
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <svg
                                className="results-doc-icon"
                                width="18"
                                height="18"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  d="M11.5 2.5H6a1.5 1.5 0 00-1.5 1.5v12A1.5 1.5 0 006 17.5h8a1.5 1.5 0 001.5-1.5V6.5l-4-4z"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M11.5 2.5v4h4M7.5 11h5M7.5 13.5h5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="results-doc-text">
                                <span className="results-doc-label">
                                  {doc.label}
                                </span>
                                {/* Where it came from, because a filing on a
                                    public register and a PDF the supplier sent
                                    us are not the same evidence. */}
                                
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {credentials.statement && (
                    <div
                      className={`results-statement${hasBusinessCerts || hasComplianceDocs ? "" : " results-statement-solo"}`}
                    >
                      {/* With nothing above it the panel title already says this. */}
                      {(hasBusinessCerts || hasComplianceDocs) && (
                        <div className="results-statement-title">
                          Our Sustainability Journey
                        </div>
                      )}
                      <p className="results-statement-body">
                        {credentials.statement}
                      </p>
                    </div>
                  )}
                  {supplierProfileUrl && (
                    <a
                      className="results-profile-link"
                      href={supplierProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View {supplierName ? `${supplierName}'s` : "the"} full compliance profile
                      <span aria-hidden="true"> →</span>
                    </a>
                  )}
                </div>
              </section>
            )}

            <section className="results-panel">
              <h2 className="results-panel-title text-center">Key Data</h2>
              <div className="results-card">
                <div className="results-key-data-sections">
                  {/* Product Characteristics */}
                  <div className="results-key-data-section">
                    <h4 className="results-key-data-section-title">
                      Product characteristics
                    </h4>
                    <ul className="results-key-data-bullet-list">
                      <li>
                        <strong>Total product weight:</strong>{" "}
                        {formData?.productInfo?.weight
                          ? `${(formData.productInfo.weight / 1000).toFixed(2)} kg`
                          : "N/A"}
                      </li>
                      <li>
                        <strong>Materials:</strong> {getMaterials()}
                      </li>
                      {formData?.decorationProcesses &&
                        Array.isArray(formData.decorationProcesses) &&
                        formData.decorationProcesses.length > 0 && (
                          <li>
                            <strong>Decoration:</strong>{" "}
                            {formData.decorationProcesses
                              .map((dec, idx) => {
                                let processName = dec.value
                                  ? dec.value
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (l) => l.toUpperCase())
                                  : `Decoration ${idx + 1}`;
  
                                // Extract stitch count from value if present (e.g., "embroidery_5000stitches")
                                let stitchCount = null;
                                if (dec.value) {
                                  const stitchMatch =
                                    dec.value.match(/(\d+)stitches?/i);
                                  if (stitchMatch) {
                                    stitchCount = parseInt(stitchMatch[1], 10);
                                    // Remove the stitch count from process name
                                    processName = processName
                                      .replace(/\d+\s*Stitches?/gi, "")
                                      .trim();
                                  }
                                }
  
                                // Use extracted stitch count or fall back to quantity
                                const displayCount = stitchCount || dec.quantity;
                                const quantityText = displayCount
                                  ? ` (approx. ${displayCount.toLocaleString()} stitches)`
                                  : "";
                                return `${processName}${quantityText}`;
                              })
                              .join(", ")}
                          </li>
                        )}
                    </ul>
                  </div>
  
                  {/* Packaging */}
                  {formData?.packaging?.materials &&
                    Array.isArray(formData.packaging.materials) &&
                    formData.packaging.materials.length > 0 && (
                      <div className="results-key-data-section">
                        <h4 className="results-key-data-section-title">
                          Packaging
                        </h4>
                        <ul className="results-key-data-bullet-list">
                          {formData.packaging.materials.map((pkg, idx) => {
                            const pkgType =
                              pkg.type ||
                              (idx === 0
                                ? "Primary packaging"
                                : `Packaging ${idx + 1}`);
                            const weight = pkg.weight
                              ? `${(pkg.weight / 1000).toFixed(2)} kg per unit`
                              : "";
                            const material = pkg.material || "";
                            return (
                              <li key={idx}>
                                <strong>{pkgType}</strong> {material}{" "}
                                {weight && `(${weight})`}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
  
                  {/* Transport Scenario */}
                  {formData?.assemblyDistribution?.transportation && (
                    <div className="results-key-data-section">
                      <h4 className="results-key-data-section-title">
                        Transport scenario
                      </h4>
                      <ul className="results-key-data-bullet-list">
                        {(() => {
                          const legs = Array.isArray(
                            formData.assemblyDistribution.transportation,
                          )
                            ? formData.assemblyDistribution.transportation
                            : [formData.assemblyDistribution.transportation];
                          const modeMap = {
                            air_international: "International air freight",
                            air_domestic: "Domestic air freight",
                            sea_container: "Sea container",
                            sea_bulk: "Sea bulk",
                            road: "Road transport",
                            rail: "Rail transport",
                            // Display-only; carries no emission factor and is
                            // never offered in the wizard. Used where a source
                            // assessment reported a combined route.
                            mixed: "Mixed modes",
                          };
                          const formatMode = (m) =>
                            m
                              ? modeMap[m] ||
                                m
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase())
                              : "N/A";
                          if (legs.length === 1) {
                            return (
                              <>
                                <li>
                                  <strong>Mode:</strong> {formatMode(legs[0]?.mode)}
                                </li>
                                {legs[0]?.distance && (
                                  <li>
                                    <strong>Distance:</strong>{" "}
                                    {Number(legs[0].distance).toLocaleString()} km
                                  </li>
                                )}
                              </>
                            );
                          }
                          return legs.map((leg, i) => (
                            <li key={i}>
                              <strong>Leg {i + 1}:</strong> {formatMode(leg.mode)}
                              {leg.distance
                                ? ` (${Number(leg.distance).toLocaleString()} km)`
                                : ""}
                            </li>
                          ));
                        })()}
                      </ul>
                    </div>
                  )}
  
                  {/* Manufacturing Energy. energySource falls back to "grid" when
                      building formData, so on an externally assessed product with
                      no energy data this panel would claim a grid mix we never
                      modelled — the manufacturing figure came from the source
                      assessment. Drop the section rather than assert it. */}
                  {!(formData?.productInfo?.isExternallyAssessed &&
                    !assessment?.energy_source) && (
                    <div className="results-key-data-section">
                      <h4 className="results-key-data-section-title">
                        Manufacturing energy
                      </h4>
                      <ul className="results-key-data-bullet-list">
                        <li>
                          <strong>Manufacturing country:</strong>{" "}
                          {formData?.materialAcquisition?.supplierLocation ||
                            formData?.assemblyDistribution?.manufacturing
                              ?.assemblyLocation ||
                            "N/A"}
                        </li>
                        <li>
                          <strong>Energy mix:</strong>{" "}
                          {formData?.assemblyDistribution?.manufacturing?.energySource
                            ? formData.assemblyDistribution.manufacturing.energySource
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase()) +
                              (formData.assemblyDistribution.manufacturing
                                .energySource === "grid"
                                ? ", mixed sources"
                                : "")
                            : "N/A"}
                        </li>
                      </ul>
                    </div>
                  )}

                  {/* Assessment source — shown only for footprints calculated
                      outside Zilch, so the reader can see who produced the
                      figures and open the methodology they came from. */}
                  {formData?.productInfo?.isExternallyAssessed && (
                    <div className="results-key-data-section">
                      <h4 className="results-key-data-section-title">
                        Assessment source
                      </h4>
                      <ul className="results-key-data-bullet-list">
                        <li>
                          <strong>Assessed by:</strong>{" "}
                          {formData.productInfo.externalAssessmentSource ||
                            "Third party"}
                        </li>
                        <li>
                          <strong>Basis:</strong> Third-party assessment,
                          reproduced as supplied
                        </li>
                        {(() => {
                          // A copy in our own bucket wins over an external link:
                          // it can't be moved or revoked from under the report.
                          const { externalAssessmentDocPath: docPath } =
                            formData.productInfo;
                          const rawUrl = formData.productInfo.externalAssessmentDocUrl;
                          // Only http(s) — this renders as a link on a public
                          // page, so never let a javascript:/data: URI through.
                          const safeUrl = /^https?:\/\//i.test(rawUrl || "")
                            ? rawUrl
                            : null;
                          const href = docPath
                            ? `${API_BASE_URL}/cert-files?path=${encodeURIComponent(docPath)}`
                            : safeUrl;
                          if (!href) return null;
                          return (
                            <li>
                              <strong>Methodology:</strong>{" "}
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {formData.productInfo.externalAssessmentDocName ||
                                  "View the full assessment"}
                              </a>
                            </li>
                          );
                        })()}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Names what's below rather than just pointing down, so the cue is worth
          reading even for someone who already knows the page scrolls. */}
      <button
        type="button"
        className={`results-scroll-cue${showScrollCue ? "" : " results-scroll-cue-hidden"}`}
        onClick={scrollToPanels}
        aria-hidden={!showScrollCue}
        tabIndex={showScrollCue ? 0 : -1}
      >
        <span>
          {hasOrgPanel ? `${orgPanelTitle} & Key Data` : "Key Data"}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M5 8L10 13L15 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="results-footer">
        <div className="results-footer-center">
          <div className="results-footer-brand">
            <span>Powered by</span>
            <img
              width={25}
              height={25}
              src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
              alt="Zilch Logo"
              className="results-footer-logo"
            />
            <a
              href="https://tryzilch.com"
              className="zilch-text-green"
              target="_blank"
              rel="noopener noreferrer"
            >
              Zilch
            </a>
          </div>
          <p className="results-footer-text">
            Transparent carbon footprint assessment for your products
          </p>
        </div>
        {supplierInfo && (
          <div className="results-download-section">
            {billing?.can_download_pdf && !billing?.download_requires_payment ? (
              <button
                className="results-btn-download"
                onClick={handleDownloadPDF}
                disabled={generatingPDF}
              >
                {generatingPDF ? (
                  <>
                    <div className="results-spinner-small"></div>
                    <span>Generating PDF...</span>
                  </>
                ) : (
                  <>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 13V3M10 13L6 9M10 13L14 9M3 16H17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Download PDF</span>
                  </>
                )}
              </button>
            ) : billing?.status === 'trialing' && supplierInfo?.role !== 'member' ? (
              <button
                className="results-btn-download results-btn-upgrade"
                onClick={handleEndTrialAndDownload}
                disabled={endingTrial || generatingPDF}
              >
                {endingTrial || generatingPDF ? (
                  <>
                    <div className="results-spinner-small"></div>
                    <span>Starting your subscription...</span>
                  </>
                ) : (
                  <>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 13V3M10 13L6 9M10 13L14 9M3 16H17"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>End trial &amp; subscribe to download</span>
                  </>
                )}
              </button>
            ) : (
              <button
                className="results-btn-download results-btn-upgrade"
                onClick={() => navigate("/billing")}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 13V3M10 13L6 9M10 13L14 9M3 16H17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Upgrade to download audit-ready PDF report</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}